import { Injectable, inject } from "@angular/core";
import { signal } from "@angular/core";
import { ApiService } from "../../../services/api.service";
import { IInstructions } from "../../automation/interfaces/i-instructions";
import { IWorkflow } from "../../automation/interfaces/i-workflow";
import { ProfileService } from "../../../services/profile.service";
import { AutomationExecutionService } from "./automation-execution.service";
import { AutomationStateService } from "./automation-state.service";
import { ChatSessionService } from "./chat-session.service";
import { AlertService } from "../../../shared/alert/alert.service";
import { AlertEnum } from "../../../shared/alert/alert.enum";

/**
 * Service for managing Automation Library drawer state and content
 */
@Injectable({
  providedIn: "root",
})
export class PromptLibraryService {
  #apiService = inject(ApiService);
  #profileService = inject(ProfileService);
  #automationExecutionService = inject(AutomationExecutionService);
  #chatSessionService = inject(ChatSessionService);
  #alertService = inject(AlertService);
  // data for the Automation Library
  instructions = signal<IInstructions[]>([]);
  workflows = signal<IWorkflow[]>([]);
  // user profile
  canManageWorkflows = signal<boolean>(false);

  constructor() {
    this.canManageWorkflows.set(
      this.#profileService.userProfile()?.config?.canManageWorkflows || false
    );
  }

  /**
   * Load instructions from the API
   */
  loadInstructions() {
      this.#apiService.getInstructions().subscribe((instructions) => {
        this.instructions.set(instructions);
      });
  }

  /**
   * Load workflows from the API
   */
  loadWorkflows() {
      this.#apiService.getWorkflows().subscribe((workflows) => {
        this.workflows.set(workflows);
      });
  }

  /**
   * Updates the chat session configuration with the selected instruction from the Automation Library
   *
   * @param instruction The selected instruction from the Automation Library to use for updating the chat configuration
   * @returns The updated chat session configuration
   */
  async updateChatSessionWithSelectedPrompt(instruction: IInstructions): Promise<void> {    
    // Create a saved session configuration before updating
    this.#automationExecutionService.saveSessionSnapshotConfiguration();

    // Update the chat session configuration with the instruction configuration
    await this.#automationExecutionService.updateChatSessionConfiguration(instruction);
  }

  /**
   * Sets the selected prompt text for the current session
   * 
   * @param promptText The prompt text to set for the current session
   * @param instruction The instruction context associated with this prompt
   */
  setSelectedPromptForCurrentSession(promptText: string, instruction?: IInstructions): void {
    const selectedSession = this.#chatSessionService.selectedSession();
    if (selectedSession) {
      // Get existing session input data
      const currentInputData = this.#chatSessionService.getSessionInputData(selectedSession.id);
      
      // Update the text while preserving documents
      this.#chatSessionService.setSessionInputData(selectedSession.id, {
        text: promptText,
        documents: currentInputData.documents,
        instruction: instruction // Store the instruction context in the session input data
      });
    }
  }

  /**
   * Removes the selected prompt text for the current session
   * Reverts the effects of setSelectedPromptForCurrentSession
   */
  removeSelectedPromptForCurrentSession(showAlert = false, alertMessage?: string): void {
    const selectedSession = this.#chatSessionService.selectedSession();
    if (selectedSession) {
      // Get existing session input data
      const currentInputData = this.#chatSessionService.getSessionInputData(selectedSession.id);
      
      // Reset the text and instruction while preserving documents
      this.#chatSessionService.setSessionInputData(selectedSession.id, {
        text: '',
        documents: currentInputData.documents,
        instruction: undefined // Remove the instruction context
      });
      
    // Show notification removal was due to sensitivity label mismatch
    if (showAlert && alertMessage) {
        this.#alertService.showAlert({
          message: alertMessage,
          type: AlertEnum.warn,
          duration: 7 // Show for 7 seconds since this is important security information
        });
      }
    }
  }

  /**
   * Checks if there is a selected prompt for the given session ID
   *
   * @param sessionId The ID of the session to check
   * @returns True if there is a selected prompt for the session, false otherwise
   */
  hasSelectedPromptForSession(sessionId: string): boolean {
    const sessions = this.#chatSessionService.chatSessions();
    const selectedSession = sessions?.find(session => session.id === sessionId);
    if (selectedSession) {
      const currentInputData = this.#chatSessionService.getSessionInputData(selectedSession.id);
      return currentInputData.instruction !== undefined;
    }
    return false;
  }
}
